import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiGlobe, FiHome, FiPhoneCall } from "react-icons/fi";
import { GiCrystalBall } from "react-icons/gi";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import PageHero from "../components/PageHero";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import { GAURI } from "../utils/siteData";
import { useServices } from "../hooks/useServices";
import { buildWhatsAppLink } from "../utils/whatsapp";

const STEPS = [
  { step: "1", title: "Share Details", desc: "Provide your birth date, time, place (or palm photos for palmistry)." },
  { step: "2", title: "Chart Preparation", desc: "Your accurate horoscope or palm analysis is carefully prepared." },
  { step: "3", title: "Consultation", desc: "Detailed discussion of your guidance, predictions and remedies." },
  { step: "4", title: "Remedy Support", desc: "Ongoing support and complete puja material arrangement if remedies are advised." },
];

export default function GauriServices() {
  const { astrologyServices, loading } = useServices("astrology");

  const enquire = (serviceTitle) => {
    const link = buildWhatsAppLink(
      GAURI.whatsapp,
      `Namaste 🙏, I would like to enquire about *${serviceTitle}*. Please share more details.`
    );
    window.open(link, "_blank");
  };

  const bookLink = buildWhatsAppLink(GAURI.whatsapp, "Namaste 🙏, I would like to book an astrology consultation.");

  return (
    <div>
      <SEO
        title="Astrology & Palmistry Services by Gauri Shastri"
        description="Gauri Shastri offers horoscope preparation, accurate guidance and remedies, marriage matching, business guidance and palmistry reading in Thane and worldwide."
        keywords="astrologer thane, palmist thane, jyotish thane, kundali milan thane, horoscope reading thane, hast rekha shastra"
        path="/astrology-palmistry-services"
      />

      <PageHero
        eyebrow="Gauri Shastri"
        title="Astrology & Palmistry Services"
        subtitle="Accurate horoscope readings, palm analysis and effective remedies - guiding you through life's most important decisions."
      >
        <div className="flex justify-center gap-4">
          <a href={`tel:+91${GAURI.phone}`} className="glass-card inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full font-semibold transition-colors">
            <FiPhoneCall /> {GAURI.phoneDisplay}
          </a>
          <a
            href={bookLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-2.5 rounded-full font-semibold shadow-premium-lg transition-colors"
          >
            <FaWhatsapp /> WhatsApp
          </a>
        </div>
      </PageHero>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle eyebrow="Special Services" title="Astrology & Palmistry Offerings" subtitle="Detailed, accurate guidance based on traditional Jyotish Shastra and Hast Rekha Shastra." />
          {loading ? (
            <Loader />
          ) : astrologyServices.length === 0 ? (
            <EmptyState title="No services yet" subtitle="Please check back soon." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {astrologyServices.map((s, i) => (
                <ServiceCard key={s.slug} title={s.title} desc={s.desc} image={s.image} icon={<GiCrystalBall size={26} />} detailLink={s.detailPath} index={i} onEnquire={() => enquire(s.title)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-maroon-50 bg-mandala">
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <SectionTitle eyebrow="Consultation Mode" title="Available in Thane & Online Worldwide" subtitle="Whether you're in Thane or anywhere in the world, you can consult Gauri Shastri." />
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-gold-100 text-center shadow-premium hover-lift">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shadow-inner"><FiHome size={28} /></div>
              <h4 className="font-heading text-maroon-800 mb-2">In-Person Consultation</h4>
              <p className="text-sm text-gray-600">Visit for a detailed face-to-face consultation in Thane, including palmistry reading.</p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-gold-100 text-center shadow-premium hover-lift">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shadow-inner"><FiGlobe size={28} /></div>
              <h4 className="font-heading text-maroon-800 mb-2">Online Consultation</h4>
              <p className="text-sm text-gray-600">Connect via phone call or video for horoscope reading and guidance from anywhere in the world.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <SectionTitle eyebrow="How It Works" title="Getting Your Consultation is Easy" />
          <div className="relative grid sm:grid-cols-4 gap-8">
            <div className="hidden sm:block absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-gold-200 via-saffron-400 to-gold-200" />
            {STEPS.map((s) => (
              <div key={s.step} className="relative text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-saffron-500 to-saffron-600 text-white flex items-center justify-center font-heading text-lg mx-auto mb-4 shadow-gold ring-4 ring-white relative z-10">
                  {s.step}
                </div>
                <h4 className="font-heading text-maroon-800 mb-1">{s.title}</h4>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 bg-hero-gradient bg-mandala text-white text-center overflow-hidden">
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4">
          <h3 className="font-heading text-3xl mb-4">Get Clarity Through Astrology Today</h3>
          <a
            href={bookLink}
            target="_blank"
            rel="noreferrer"
            className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
          >
            <FaWhatsapp /> Book Consultation on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
